import type { IRepresentative } from "containers/Beneficiars/types";
import { IBeneficiar } from "containers/Beneficiars/types";
import { getQuarter, getYear } from "date-fns";
import type { IDeclaration } from "store/contributionStore";
import type { IPayment } from "store/paymentSlice";

import type { DeclarationsCardsData, URSSAFBeneficiaryError } from "../utils";

/**
 * Returns one error if there are several for one beneficiary (in case of several representatives).
 * Mostly needed to know number of incorrect beneficiaries.
 * @param errors
 * @returns Array with errors.
 */
export const getReducedPayments = (payments: IPayment[]) => {
    if (!payments) return [];
    const reducedPayments = payments.reduce((acc: IPayment[], payment: IPayment) => {
        const beneficiaryId = payment.beneficiary.id;
        const existingBeneficiary =
            beneficiaryId &&
            acc?.find((accItem) => {
                return accItem.beneficiary.id.toString().includes(beneficiaryId?.toString());
            });
        if (!existingBeneficiary) acc?.push(payment);
        return acc as IPayment[];
    }, []);

    return reducedPayments;
};

/**
 * Returns one error if there are several for one beneficiary (in case of several representatives).
 * Mostly needed to know number of incorrect beneficiaries.
 * @param errors
 * @returns Array with errors.
 */
export const getReducedErrors = (errors: URSSAFBeneficiaryError[]) => {
    const reducedErrors = errors.reduce((acc: URSSAFBeneficiaryError[] | undefined, error: URSSAFBeneficiaryError) => {
        const beneficiaryId = error.beneficiary.beneficiaryId;
        const existingBeneficiary =
            beneficiaryId &&
            acc?.find((accItem) => accItem.beneficiaryId.toString().includes(beneficiaryId?.toString()));
        if (!existingBeneficiary) acc?.push(error);
        return acc as URSSAFBeneficiaryError[];
    }, []);

    return reducedErrors;
};

/**
 * Returns names of representatives joine by comma.
 * @param representatives Representative data as saved in IPayment.
 */
export const formatRepresentativesNames = (representatives: IRepresentative[]) => {
    const allNames = representatives.map((item) => `${(item.surname ?? "").toUpperCase()} ${item.name ?? ""}`);
    return allNames.join(", ");
};

/**
 * Retrieves errors belonging to a payment (errors from beneficiary or representative).
 * @param beneficiary Beneficiary data as saved in IPayment.
 * @params errors All errors.
 * @params representativeId Id of the representative linked to payment. If not null, it means we have to
 * @returns Returns all errors data linked to the payment.
 */
export const getBeneficiaryErrors = ({
    payment,
    errors,
}: {
    payment: IPayment;
    errors: Map<string, URSSAFBeneficiaryError>;
}): URSSAFBeneficiaryError | undefined => {
    const beneficiaryId = payment.beneficiary.id;
    const representativeId = payment._representative.id;
    if (!!payment.computeFromRepresentative && representativeId) {
        return errors.get(`${beneficiaryId}-${representativeId}`);
    } else {
        return errors.get(String(beneficiaryId));
    }
};

export const getPaymentsInDeclaration = ({
    payments,
    card,
}: {
    payments: IPayment[];
    card: DeclarationsCardsData | IDeclaration | undefined;
}): IPayment[] => {
    if (!payments.length || !card) return [];
    return payments.filter((item) => item.grossValue > 0 && item.netRemainingToPay !== item.netValue);
};
